class FivePaisaWebSocketService {
  constructor() {
    this.ws = null;
    this.clientCode = null;
    this.accessToken = null;

    this.heartbeatInterval = null;
    this.pongTimeout = null;
    this.reconnectTimeout = null;
    this.connectTimeout = null;

    this.shouldReconnect = true;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 20;
    this.baseReconnectDelayMs = 1000;
    this.maxReconnectDelayMs = 15000;
    this.connectTimeoutMs = 12000;
    this.pongTimeoutMs = 7000;
    this.heartbeatIntervalMs = 15000;

    this.statusListeners = new Set();
    this.priceListeners = new Set();
    this.depthListeners = new Set();
    this.errorListeners = new Set();

    // Track active subscriptions so they can be replayed after reconnect.
    this.marketFeedSubscriptions = new Map();
    this.marketDepthSubscriptions = new Map();

    this.debugEnabled = true;
  }

  debug(...args) {
    if (!this.debugEnabled) return;
    console.log("debuggggg [WS-SERVICE]", ...args);
  }

  setDebug(enabled) {
    this.debugEnabled = !!enabled;
  }

  getSharedData() {
    try {
      return JSON.parse(localStorage.getItem("sharedData") || "null");
    } catch (_) {
      return null;
    }
  }

  getSocketState() {
    return this.ws?.readyState;
  }

  isConnected() {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  // -------- Public connect/disconnect --------
  connect() {
    const shared = this.getSharedData();
    if (!shared?.accessToken || !shared?.ClientCode) {
      const err = new Error("Missing accessToken or ClientCode in sharedData");
      this.emitError(err);
      this.debug("connect aborted: missing auth");
      return;
    }

    this.accessToken = shared.accessToken;
    this.clientCode = shared.ClientCode;
    this.shouldReconnect = true;

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.debug("connect skipped: socket already open");
      return;
    }
    if (this.ws?.readyState === WebSocket.CONNECTING) {
      this.debug("connect skipped: socket already connecting");
      return;
    }

    this.openSocket();
  }

  disconnect({ manual = true } = {}) {
    this.shouldReconnect = !manual;
    this.clearTimers();

    if (this.ws) {
      try {
        this.ws.close(1000, manual ? "manual disconnect" : "disconnect");
      } catch (_) {}
    }

    this.ws = null;
    this.emitStatus({
      type: "disconnected",
      manual,
      readyState: WebSocket.CLOSED,
    });
  }

  // -------- Public subscriptions --------
  subscribeMarketFeed(items = []) {
    const normalized = this.normalizeMarketFeedItems(items);
    if (!normalized.length) return;

    normalized.forEach((item) => {
      this.marketFeedSubscriptions.set(
        this.feedKey(item.Exch, item.ExchType, item.ScripCode),
        item,
      );
    });

    if (this.isConnected()) {
      this.sendMarketFeed("Subscribe", normalized);
    } else {
      this.debug("queued market feed subscription", normalized.length);
      this.connect();
    }
  }

  unsubscribeMarketFeed(items = []) {
    const normalized = this.normalizeMarketFeedItems(items);
    if (!normalized.length) return;

    normalized.forEach((item) => {
      this.marketFeedSubscriptions.delete(
        this.feedKey(item.Exch, item.ExchType, item.ScripCode),
      );
    });

    if (this.isConnected()) {
      this.sendMarketFeed("Unsubscribe", normalized);
    }
  }

  subscribeMarketDepth(items = []) {
    const normalized = this.normalizeMarketFeedItems(items);
    if (!normalized.length) return;

    normalized.forEach((item) => {
      this.marketDepthSubscriptions.set(
        this.feedKey(item.Exch, item.ExchType, item.ScripCode),
        item,
      );
    });

    if (this.isConnected()) {
      this.sendMarketDepth("Subscribe", normalized);
    } else {
      this.debug("queued market depth subscription", normalized.length);
      this.connect();
    }
  }

  unsubscribeMarketDepth(items = []) {
    const normalized = this.normalizeMarketFeedItems(items);
    if (!normalized.length) return;

    normalized.forEach((item) => {
      this.marketDepthSubscriptions.delete(
        this.feedKey(item.Exch, item.ExchType, item.ScripCode),
      );
    });

    if (this.isConnected()) {
      this.sendMarketDepth("Unsubscribe", normalized);
    }
  }

  // -------- Public listeners --------
  onStatus(listener) {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  onPrice(listener) {
    this.priceListeners.add(listener);
    return () => this.priceListeners.delete(listener);
  }

  onDepth(listener) {
    this.depthListeners.add(listener);
    return () => this.depthListeners.delete(listener);
  }

  onError(listener) {
    this.errorListeners.add(listener);
    return () => this.errorListeners.delete(listener);
  }

  // -------- Internal socket lifecycle --------
  openSocket() {
    this.clearConnectTimeout();
    this.clearReconnectTimeout();

    const url = `wss://openfeed.5paisa.com/Feeds/api/chat?Value1=${this.accessToken}|${this.clientCode}`;
    this.debug("opening socket", { url, attempt: this.reconnectAttempts + 1 });

    this.ws = new WebSocket(url);
    this.emitStatus({
      type: "connecting",
      readyState: this.ws.readyState,
      attempt: this.reconnectAttempts + 1,
    });

    this.connectTimeout = setTimeout(() => {
      if (this.ws?.readyState === WebSocket.CONNECTING) {
        this.debug("connect timeout -> closing connecting socket");
        try {
          this.ws.close();
        } catch (_) {}
      }
    }, this.connectTimeoutMs);

    this.ws.onopen = () => {
      this.clearConnectTimeout();
      this.reconnectAttempts = 0;
      this.debug("socket open");
      this.emitStatus({ type: "connected", readyState: this.ws.readyState });
      this.startHeartbeat();
      this.replaySubscriptions();
    };

    this.ws.onmessage = (event) => {
      this.handleMessage(event.data);
    };

    this.ws.onerror = (event) => {
      this.debug("socket error");
      this.emitError(event);
    };

    this.ws.onclose = (event) => {
      this.clearConnectTimeout();
      this.stopHeartbeat();
      this.clearPongTimeout();

      this.debug("socket close", {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
      });
      this.emitStatus({
        type: "closed",
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
      });

      if (!this.shouldReconnect) return;
      this.scheduleReconnect();
    };
  }

  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.debug("max reconnect attempts reached");
      this.emitStatus({ type: "reconnect-giveup" });
      return;
    }

    const attempt = this.reconnectAttempts + 1;
    const delay = Math.min(
      this.maxReconnectDelayMs,
      this.baseReconnectDelayMs * 2 ** this.reconnectAttempts,
    );

    this.reconnectAttempts += 1;
    this.debug("scheduling reconnect", { attempt, delay });
    this.emitStatus({ type: "reconnecting", attempt, delay });

    this.reconnectTimeout = setTimeout(() => {
      this.openSocket();
    }, delay);
  }

  // -------- Internal messaging --------
  handleMessage(rawData) {
    let parsed;
    try {
      parsed = JSON.parse(rawData);
    } catch (err) {
      this.emitError(err);
      return;
    }

    // Heartbeat response
    if (parsed?.Method === "Heartbeat") {
      this.clearPongTimeout();
      this.emitStatus({ type: "heartbeat-ack" });
      return;
    }

    // Market depth payload (5paisa shape)
    if (parsed?.Details && Array.isArray(parsed.Details) && parsed?.Token) {
      this.priceListeners.forEach(() => {}); // no-op to avoid accidental dead code removal
      this.depthListeners.forEach((cb) => cb(parsed));
      return;
    }

    // Market feed ticks often come as array
    if (Array.isArray(parsed) && parsed[0]) {
      this.priceListeners.forEach((cb) => cb(parsed[0], parsed));
      return;
    }

    // Fallback status stream
    this.emitStatus({ type: "raw", payload: parsed });
  }

  sendMarketFeed(operation, items) {
    if (!this.isConnected() || !items.length) return;
    const payload = {
      Method: "MarketFeedV3",
      Operation: operation,
      ClientCode: this.clientCode,
      MarketFeedData: items,
    };
    this.ws.send(JSON.stringify(payload));
    this.debug("market feed sent", { operation, count: items.length });
  }

  sendMarketDepth(operation, items) {
    if (!this.isConnected() || !items.length) return;
    const payload = {
      Method: "MarketDepthService",
      Operation: operation,
      ClientCode: this.clientCode,
      MarketFeedData: items,
    };
    this.ws.send(JSON.stringify(payload));
    this.debug("market depth sent", { operation, count: items.length });
  }

  replaySubscriptions() {
    const feedItems = Array.from(this.marketFeedSubscriptions.values());
    const depthItems = Array.from(this.marketDepthSubscriptions.values());

    if (feedItems.length) this.sendMarketFeed("Subscribe", feedItems);
    if (depthItems.length) this.sendMarketDepth("Subscribe", depthItems);

    this.debug("replayed subscriptions", {
      marketFeed: feedItems.length,
      marketDepth: depthItems.length,
    });
  }

  // -------- Heartbeat --------
  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (!this.isConnected()) return;
      try {
        this.ws.send(
          JSON.stringify({
            Method: "Heartbeat",
            Operation: "Ping",
            Timestamp: Date.now(),
          }),
        );
        this.emitStatus({ type: "heartbeat-sent" });

        this.clearPongTimeout();
        this.pongTimeout = setTimeout(() => {
          this.debug("pong timeout -> closing socket");
          try {
            this.ws?.close();
          } catch (_) {}
        }, this.pongTimeoutMs);
      } catch (err) {
        this.emitError(err);
      }
    }, this.heartbeatIntervalMs);
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  // -------- Helpers --------
  normalizeMarketFeedItems(items) {
    return (items || [])
      .map((item) => {
        const Exch = String(item.Exch ?? item.exch ?? "").charAt(0);
        const ExchType = String(item.ExchType ?? item.exchType ?? "").charAt(0);
        const ScripCode = Number(item.ScripCode ?? item.scripcode ?? item.Scripcode ?? 0);
        if (!Exch || !ExchType || !ScripCode) return null;
        return { Exch, ExchType, ScripCode };
      })
      .filter(Boolean);
  }

  feedKey(exch, exchType, scripCode) {
    return `${exch}|${exchType}|${scripCode}`;
  }

  clearConnectTimeout() {
    if (this.connectTimeout) {
      clearTimeout(this.connectTimeout);
      this.connectTimeout = null;
    }
  }

  clearReconnectTimeout() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  clearPongTimeout() {
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
  }

  clearTimers() {
    this.clearConnectTimeout();
    this.clearReconnectTimeout();
    this.clearPongTimeout();
    this.stopHeartbeat();
  }

  emitStatus(payload) {
    this.statusListeners.forEach((cb) => {
      try {
        cb(payload);
      } catch (_) {}
    });
  }

  emitError(error) {
    this.errorListeners.forEach((cb) => {
      try {
        cb(error);
      } catch (_) {}
    });
  }
}

const fivePaisaWebSocketService = new FivePaisaWebSocketService();

export default fivePaisaWebSocketService;
